import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiSend, FiCheck, FiX } from 'react-icons/fi';

const emptyForm = {
  label: 'Platform Default',
  host: '',
  port: 587,
  username: '',
  password: '',
  encryption: 'tls',
  fromEmail: '',
  fromName: '',
  replyToEmail: '',
};

const PlatformEmailSettings = () => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  const loadConfig = () => {
    setLoading(true);
    apiClient.get('/platform/smtp')
      .then(({ data }) => {
        const existing = data.data;
        setConfig(existing);
        if (existing) {
          setForm({
            label: existing.label || 'Platform Default',
            host: existing.host || '',
            port: existing.port || 587,
            username: existing.username || '',
            password: '',
            encryption: existing.encryption || 'tls',
            fromEmail: existing.fromEmail || '',
            fromName: existing.fromName || '',
            replyToEmail: existing.replyToEmail || '',
          });
        }
      })
      .catch((err) => showError(getErrorMessage(err, 'Could not load SMTP configuration')))
      .finally(() => setLoading(false));
  };

  useEffect(loadConfig, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (!payload.username) delete payload.username;
      if (!payload.fromName) delete payload.fromName;
      if (!payload.replyToEmail) delete payload.replyToEmail;
      await apiClient.post('/platform/smtp', payload);
      success('✅ Platform SMTP configuration saved');
      loadConfig();
    } catch (err) { showError(getErrorMessage(err, 'Could not save configuration')); }
  };

  const handleTest = async (e) => {
    e.preventDefault();
    if (!testEmail) return;
    setTesting(true);
    try {
      const { data } = await apiClient.post('/platform/smtp/test', { toEmail: testEmail });
      if (data.data.success) {
        success('✅ Test email sent successfully');
      } else {
        showError(data.data.error || 'Test email failed to send');
      }
      loadConfig();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not send test email'));
    } finally {
      setTesting(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Platform Email Settings</h1>
        <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0' }}>
          The fallback SMTP configuration used for signup OTP emails and any organisation without its own SMTP setup
        </p>
      </div>

      {!loading && !config && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem', color: '#92400e', fontSize: '0.85rem' }}>
          ⚠️ No platform SMTP configuration exists yet — until you save one, signup OTP emails and any organisation without its own SMTP setup will fail to send.
        </div>
      )}

      {config && (
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <span className="badge" style={{ background: config.isActive ? '#10b981' : '#9ca3af', color: 'white' }}>
            {config.isActive ? 'Active' : 'Inactive'}
          </span>
          <span className="badge" style={{ background: config.hasPassword ? '#10b981' : '#ef4444', color: 'white' }}>
            {config.hasPassword ? 'Password set' : 'No password set'}
          </span>
          {config.lastTestedAt && (
            <span className="badge" style={{ background: config.lastTestStatus === 'success' ? '#10b981' : '#ef4444', color: 'white' }}>
              Last test: {config.lastTestStatus} ({new Date(config.lastTestedAt).toLocaleString()})
            </span>
          )}
        </div>
      )}

      <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.1rem', marginTop: 0, marginBottom: '1rem' }}>SMTP Configuration</h2>
        <form onSubmit={handleSave} className="form-grid">
          <div className="form-field"><label>Label</label><input value={form.label} onChange={(e) => setForm(p => ({ ...p, label: e.target.value }))} /></div>
          <div className="form-field"><label>Host *</label><input required value={form.host} onChange={(e) => setForm(p => ({ ...p, host: e.target.value }))} placeholder="smtp-relay.brevo.com" /></div>
          <div className="form-field"><label>Port *</label><input required type="number" value={form.port} onChange={(e) => setForm(p => ({ ...p, port: Number(e.target.value) }))} placeholder="587" /></div>
          <div className="form-field">
            <label>Encryption</label>
            <select value={form.encryption} onChange={(e) => setForm(p => ({ ...p, encryption: e.target.value }))}>
              <option value="tls">TLS (port 587)</option>
              <option value="ssl">SSL (port 465)</option>
              <option value="none">None</option>
            </select>
          </div>
          <div className="form-field"><label>Username</label><input value={form.username} onChange={(e) => setForm(p => ({ ...p, username: e.target.value }))} /></div>
          <div className="form-field">
            <label>Password {config?.hasPassword ? '(leave blank to keep current)' : '*'}</label>
            <input type="password" value={form.password} onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))} required={!config?.hasPassword} />
          </div>
          <div className="form-field"><label>From Email *</label><input required type="email" value={form.fromEmail} onChange={(e) => setForm(p => ({ ...p, fromEmail: e.target.value }))} placeholder="noreply@effectivestaff.co.uk" /></div>
          <div className="form-field"><label>From Name</label><input value={form.fromName} onChange={(e) => setForm(p => ({ ...p, fromName: e.target.value }))} placeholder="Effective Global Staff" /></div>
          <div className="form-field"><label>Reply-To Email</label><input type="email" value={form.replyToEmail} onChange={(e) => setForm(p => ({ ...p, replyToEmail: e.target.value }))} /></div>
          <div className="form-field full-width form-actions">
            <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Save Configuration</button>
          </div>
        </form>
      </div>

      {config && (
        <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', marginTop: 0, marginBottom: '1rem' }}>Send Test Email</h2>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Confirm the configuration above actually works before relying on it for real signups.
          </p>
          <form onSubmit={handleTest} style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="email"
              required
              placeholder="your@email.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              style={{ flex: 1, padding: '0.5rem', maxWidth: '320px' }}
            />
            <button type="submit" className="btn btn-primary" style={{ width: 'auto' }} disabled={testing}>
              <FiSend size={14} /> {testing ? 'Sending...' : 'Send Test'}
            </button>
          </form>
        </div>
      )}
    </motion.div>
  );
};

export default PlatformEmailSettings;